import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import type { ConnectionResponse, ConnectionsResponse } from '@lifetimeline/shared';

import { AutoLoad } from '../components/AutoLoad.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { BackLink } from '../components/BackLink.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { PersonList, PersonRow } from '../components/PersonList.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { useAuth } from '../features/auth/AuthContext.js';
import { useI18n } from '../i18n/index.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import styles from './pageStyles.module.css';

/**
 * 이어진 사람 — 프로필의 숫자를 누르면 온다.
 *
 * **받은 신청을 맨 위**에 둔다. 내 대답을 기다리는 유일한 항목이고, 묻혀
 * 있으면 상대는 영영 기다린다.
 */
export function ConnectionsPage() {
  const { t } = useI18n();
  const { status } = useAuth();
  const toast = useToast();

  /*
   * 상태를 **따로** 읽는다. 오간 요청은 내 대답을 기다리는 것이라 한 화면에
   * 다 보여야 하고, 이어진 인연은 수백이 될 수 있어 한 쪽씩 읽는다.
   */
  const [pending, setPending] = useState<ConnectionResponse[] | null>(null);
  const [joined, setJoined] = useState<ConnectionResponse[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [waiting, accepted] = await Promise.all([
        api.get<ConnectionsResponse>('/api/me/connections?status=pending'),
        api.get<ConnectionsResponse>('/api/me/connections?status=accepted'),
      ]);
      setPending(waiting.connections);
      setJoined(accepted.connections);
      setCursor(accepted.nextCursor);
      setError(null);
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    }
  }, [t]);

  /**
   * 다음 쪽을 **이어 붙인다.**
   *
   * 커서는 "몇 번째부터"가 아니라 "어디서부터"다 — 보는 동안 앞에서 인연이
   * 생기거나 사라져도 건너뛰거나 두 번 보이지 않는다.
   */
  const loadMore = async () => {
    if (cursor === null || more) {
      return;
    }
    setMore(true);
    try {
      const next = await api.get<ConnectionsResponse>(
        `/api/me/connections?status=accepted&cursor=${encodeURIComponent(cursor)}`,
      );
      setJoined((current) => [...(current ?? []), ...next.connections]);
      setCursor(next.nextCursor);
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setMore(false);
    }
  };

  useEffect(() => {
    if (status === 'authenticated') {
      void load();
    }
  }, [status, load]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      </Layout>
    );
  }
  if (status !== 'authenticated') {
    return <Navigate to="/signin" replace />;
  }

  const connected = joined ?? [];
  const incoming = (pending ?? []).filter((row) => row.direction === 'incoming');
  const outgoing = (pending ?? []).filter((row) => row.direction === 'outgoing');

  const act = async (call: () => Promise<unknown>, message: Parameters<typeof t>[0]) => {
    try {
      await call();
      toast.show(t(message));
      await load();
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    }
  };

  const path = (handle: string) => `/api/connections/${encodeURIComponent(handle)}`;

  return (
    <Layout>
      <div className={styles.head}>
        <BackLink />
        <h1 className={styles.title}>{t('connection.title')}</h1>
      </div>

      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      {/*
        차례가 곧 급한 순서다.
          ① 나에게 온 요청 — 내 대답을 기다리는 유일한 것. 묻히면 상대가 영영 기다린다
          ② 내가 보낸 요청 — 아직 끝나지 않은 내 일
          ③ 이어진 인연   — 이미 끝난 일이라 맨 아래
      */}
      {incoming.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('connection.incoming')}</h2>
          <PersonList>
            {incoming.map((row) => (
              <PersonRow
                key={row.handle}
                person={row}
                action={
                  <>
                    <Button
                      size="sm"
                      onClick={() =>
                        void act(
                          () => api.post(`${path(row.handle)}/accept`, {}),
                          'connection.accepted',
                        )
                      }
                    >
                      {t('connection.accept')}
                    </Button>
                    {/* 수락 옆이라 색으로 갈라 둬야 헛누르지 않는다 */}
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        void act(() => api.delete(path(row.handle)), 'connection.rejected')
                      }
                    >
                      {t('connection.reject')}
                    </Button>
                  </>
                }
              />
            ))}
          </PersonList>
        </section>
      ) : null}

      {outgoing.length > 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('connection.outgoing')}</h2>
          <PersonList>
            {outgoing.map((row) => (
              <PersonRow
                key={row.handle}
                person={row}
                action={
                  /*
                    글자가 **하는 일**을 말한다. "수락 기다리는 중"은 지금
                    상태인데, 그건 이미 위의 제목(내가 보낸 요청)이 말한다 —
                    같은 말을 두 번 하면서 정작 누르면 무슨 일이 나는지는
                    아무도 말하지 않고 있었다.
                  */
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      void act(() => api.delete(path(row.handle)), 'connection.canceled')
                    }
                  >
                    {t('connection.cancel')}
                  </Button>
                }
              />
            ))}
          </PersonList>
        </section>
      ) : null}
      <section className={styles.section}>
        {/*
          위에 다른 묶음이 있을 때만 제목을 단다.
          혼자 있을 때는 화면 제목(인연)과 **같은 말이 두 번** 찍힌다.

          한 쪽만 읽어 왔을 수 있으므로 **세지 않는다** — 제목은 이름뿐이다.
        */}
        {incoming.length > 0 || outgoing.length > 0 ? (
          <h2 className={styles.sectionTitle}>{t('connection.title')}</h2>
        ) : null}
        {connected.length === 0 ? (
          <EmptyState label={t('connection.none')} />
        ) : (
          <>
            <PersonList>
              {connected.map((row) => (
                <PersonRow key={row.handle} person={row} />
              ))}
            </PersonList>
            {/* 목록도 같은 규칙 — 스크롤이 닿으면 이어진다 */}
            {cursor === null ? null : <AutoLoad onLoad={() => void loadMore()} loading={more} />}
          </>
        )}
      </section>
    </Layout>
  );
}
